import React, { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaCheck,
  FaCheckCircle,
  FaEnvelope,
  FaMobileAlt,
  FaShieldAlt,
  FaTruck,
  FaTv,
  FaWhatsapp,
} from "react-icons/fa";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  signInWithEmailAndPassword,
} from "firebase/auth";
import ReCAPTCHA from "react-google-recaptcha";

import { auth } from "../utils/FirebaseConfig";
import { captcha_site_key } from "../utils/keys";
import "../styles/Form.css";

const STATUS_API = "https://us-central1-etikaf-admin.cloudfunctions.net/getEtikafRegistrationStatus";
const REGISTRATION_API = "https://us-central1-etikaf-admin.cloudfunctions.net/startEtikafRegistration";
const REGISTRATION_HELP_URL = `https://wa.me/601158885060?text=${encodeURIComponent("Assalamualaikum. Saya perlukan bantuan semasa pendaftaran Etikaf.")}`;
const EMAIL_ACTION_SETTINGS = {
  // Domain rasmi Firebase sentiasa dibenarkan untuk pautan tindakan Auth.
  // Custom domain admin.etikaf.com boleh digunakan semula selepas ditambah
  // dalam Firebase Authentication > Settings > Authorized domains.
  url: "https://etikaf-admin.firebaseapp.com/daftar?verified=1&intent=etikaf-my",
  handleCodeInApp: false,
};
const PACKAGE_AMOUNT = 3600;
const MALAYSIA_STATES = [
  "Johor",
  "Kedah",
  "Kelantan",
  "Melaka",
  "Negeri Sembilan",
  "Pahang",
  "Perak",
  "Perlis",
  "Pulau Pinang",
  "Sabah",
  "Sarawak",
  "Selangor",
  "Terengganu",
  "W.P. Kuala Lumpur",
  "W.P. Labuan",
  "W.P. Putrajaya",
];
const EAST_MALAYSIA_STATES = new Set(["Sabah", "Sarawak", "W.P. Labuan"]);
const getShippingFee = (state, postcode) => {
  if (!state) return 0;
  const postcodeNumber = Number(String(postcode || "").trim());
  return EAST_MALAYSIA_STATES.has(state) || postcodeNumber >= 87000 ? 50 : 25;
};

const getVerificationErrorMessage = (error) => {
  switch (error?.code) {
    case "auth/too-many-requests":
      return "Terlalu banyak percubaan dibuat. Tunggu beberapa minit, kemudian tekan Hantar semula.";
    case "auth/quota-exceeded":
      return "Had penghantaran e-mel sementara telah dicapai. Cuba lagi kemudian atau hubungi bantuan.";
    case "auth/network-request-failed":
      return "Sambungan internet terganggu. Semak rangkaian dan cuba hantar semula.";
    case "auth/unauthorized-continue-uri":
    case "auth/invalid-continue-uri":
    case "auth/missing-continue-uri":
      return "Pautan pengesahan belum dapat disediakan. Hubungi bantuan Etikaf.";
    case "auth/operation-not-allowed":
      return "Pengesahan e-mel belum diaktifkan pada sistem. Hubungi bantuan Etikaf.";
    default:
      return "Pautan pengesahan belum dapat dihantar. Cuba hantar semula atau hubungi bantuan.";
  }
};

const Field = ({ id, label, type = "text", value, onChange, autoComplete, disabled = false }) => (
  <div className="input-container">
    <input
      type={type}
      id={id}
      value={value}
      onChange={onChange}
      required
      disabled={disabled}
      className="input-field"
      placeholder=" "
      autoComplete={autoComplete}
    />
    <label htmlFor={id} className="floating-label">{label}</label>
  </div>
);

const SelectField = ({ id, label, value, onChange, options }) => (
  <div className="input-container">
    <select id={id} value={value} onChange={onChange} required className="input-field registration-select">
      <option value="" disabled>{label}</option>
      {options.map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
    <label htmlFor={id} className="floating-label">{label}</label>
  </div>
);

const Daftar = () => {
  const [step, setStep] = useState(1);
  const [namaMasjid, setNamaMasjid] = useState("");
  const [emailMasjid, setEmailMasjid] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [namaAnda, setNamaAnda] = useState("");
  const [noTelAnda, setNoTelAnda] = useState("");
  const [alamat, setAlamat] = useState("");
  const [poskod, setPoskod] = useState("");
  const [bandar, setBandar] = useState("");
  const [negeri, setNegeri] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationDeliveryFailed, setVerificationDeliveryFailed] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationBusy, setVerificationBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const currentYear = new Date().getFullYear();
  const shippingFee = getShippingFee(negeri, poskod);
  const totalAmount = PACKAGE_AMOUNT + shippingFee;

  const storePendingRegistration = (data, fallbackEmail) => {
    const pending = {
      id: data.submitterId || "",
      source: "secure_status",
      status: data.status || "pending_payment",
      billUrl: data.billUrl || "",
      namaMasjid: data.namaMasjid || "Pendaftaran Etikaf",
      emailMasjid: data.emailMasjid || data.email || fallbackEmail || "",
      packageAmount: Number(data.packageAmount || PACKAGE_AMOUNT),
      shippingFee: Number(data.shippingFee || 0),
      totalAmount: Number(data.totalAmount || data.amount || data.packageAmount || PACKAGE_AMOUNT),
    };
    localStorage.setItem("pendingRegistration", JSON.stringify(pending));
    localStorage.setItem("submittedEmail", pending.emailMasjid);
    if (pending.id) localStorage.setItem("submitterId", pending.id);
  };

  const checkRegistrationState = async (user) => {
    setVerificationBusy(true);
    setError("");

    try {
      const idToken = await user.getIdToken(true);
      const response = await fetch(STATUS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Status pendaftaran tidak dapat disemak.");

      if (data.state === "active") {
        setNotice("Akaun ini sudah aktif. Membuka Sistem Admin...");
        window.location.href = "/";
        return;
      }

      if (data.state === "pending") {
        storePendingRegistration(data, user.email);
        window.location.href = "/status";
        return;
      }

      setEmailMasjid((user.email || emailMasjid).toLowerCase());
      setEmailVerified(true);
      setVerificationSent(false);
      setVerificationDeliveryFailed(false);
      setNotice("Emel berjaya disahkan. Teruskan maklumat penghantaran.");
      setStep(2);
    } catch (err) {
      console.error("Ralat status pendaftaran:", err);
      setError(err?.message || "Status pendaftaran tidak dapat disemak.");
    } finally {
      setVerificationBusy(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      try {
        await reload(user);
        if (user.emailVerified) {
          await checkRegistrationState(user);
        } else {
          setEmailMasjid((user.email || "").toLowerCase());
          setVerificationSent(true);
        }
      } catch (err) {
        console.error("Semakan pengesahan e-mel gagal:", err);
      }
    });

    return () => unsubscribe();
    // checkRegistrationState sengaja tidak dijadikan dependency supaya listener dipasang sekali.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateStep1 = () => {
    const cleanEmail = emailMasjid.trim().toLowerCase();

    if (!cleanEmail || !cleanEmail.includes("@")) {
      setError("Masukkan emel yang sah.");
      return false;
    }
    if (password.length < 6) {
      setError("Kata laluan mesti sekurang-kurangnya 6 aksara.");
      return false;
    }
    if (password !== confirmPassword) {
      setError("Kata laluan tidak sepadan.");
      return false;
    }

    setEmailMasjid(cleanEmail);
    setError("");
    return true;
  };

  const sendVerification = async () => {
    if (!validateStep1()) return;
    setVerificationBusy(true);
    setError("");
    setNotice("");
    setVerificationDeliveryFailed(false);

    const cleanEmail = emailMasjid.trim().toLowerCase();

    try {
      let registrationUser = auth.currentUser;

      if (registrationUser?.email?.toLowerCase() !== cleanEmail) {
        if (registrationUser) await auth.signOut();

        try {
          const credential = await createUserWithEmailAndPassword(auth, cleanEmail, password);
          registrationUser = credential.user;
        } catch (createError) {
          if (createError?.code !== "auth/email-already-in-use") throw createError;

          try {
            const credential = await signInWithEmailAndPassword(auth, cleanEmail, password);
            registrationUser = credential.user;
          } catch (signInError) {
            console.error("Akaun wujud tetapi tidak dapat disambungkan:", signInError);
            setError("Emel ini sudah mempunyai akaun, tetapi kata laluan tidak sepadan. Log masuk atau gunakan Lupa kata laluan.");
            return;
          }
        }
      }

      await reload(registrationUser);
      if (registrationUser.emailVerified) {
        await checkRegistrationState(registrationUser);
        return;
      }

      try {
        await sendEmailVerification(registrationUser, EMAIL_ACTION_SETTINGS);
        setVerificationSent(true);
        setVerificationDeliveryFailed(false);
        setNotice(`Pautan pengesahan telah dihantar ke ${cleanEmail}.`);
      } catch (deliveryError) {
        console.error("Akaun dicipta tetapi e-mel pengesahan gagal dihantar:", deliveryError);
        setVerificationSent(true);
        setVerificationDeliveryFailed(true);
        setError(getVerificationErrorMessage(deliveryError));
      }
    } catch (err) {
      console.error("Ralat cipta akaun pendaftaran:", err);
      if (err?.code === "auth/invalid-email") {
        setError("Alamat e-mel tidak sah.");
      } else if (err?.code === "auth/weak-password") {
        setError("Kata laluan terlalu lemah. Gunakan sekurang-kurangnya 6 aksara.");
      } else if (err?.code === "auth/network-request-failed") {
        setError("Sambungan internet terganggu. Semak rangkaian dan cuba semula.");
      } else {
        setError("Akaun tidak dapat disediakan. Cuba semula atau hubungi bantuan Etikaf.");
      }
    } finally {
      setVerificationBusy(false);
    }
  };

  const confirmVerification = async () => {
    setVerificationBusy(true);
    setError("");

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Sesi pengesahan tidak ditemui. Buka pautan menggunakan browser yang sama.");
      await reload(user);
      if (!user.emailVerified) {
        setError("E-mel belum disahkan. Klik pautan dalam e-mel, kemudian cuba lagi.");
        return;
      }
      await checkRegistrationState(user);
    } catch (err) {
      console.error("Ralat sahkan e-mel:", err);
      setError(err?.message || "Pengesahan e-mel belum selesai.");
    } finally {
      setVerificationBusy(false);
    }
  };

  const resendVerification = async () => {
    setVerificationBusy(true);
    setError("");
    setNotice("");
    try {
      let user = auth.currentUser;
      if (!user && emailMasjid && password) {
        const credential = await signInWithEmailAndPassword(auth, emailMasjid.trim().toLowerCase(), password);
        user = credential.user;
      }
      if (!user) {
        setError("Sesi pendaftaran telah tamat. Log masuk semula untuk menyambung pengesahan.");
        return;
      }

      await reload(user);
      if (user.emailVerified) {
        await checkRegistrationState(user);
        return;
      }

      await sendEmailVerification(user, EMAIL_ACTION_SETTINGS);
      setVerificationDeliveryFailed(false);
      setNotice(`Pautan baru telah dihantar ke ${emailMasjid}.`);
    } catch (err) {
      console.error("Ralat hantar semula pengesahan:", err);
      setVerificationDeliveryFailed(true);
      if (["auth/invalid-credential", "auth/wrong-password", "auth/user-not-found"].includes(err?.code)) {
        setError("Sesi tidak dapat dipulihkan. Log masuk semula atau gunakan Lupa kata laluan.");
      } else {
        setError(getVerificationErrorMessage(err));
      }
    } finally {
      setVerificationBusy(false);
    }
  };

  const validateStep2 = () => {
    if (!emailVerified) {
      setError("Sahkan e-mel sebelum meneruskan.");
      setStep(1);
      return false;
    }
    if (!namaMasjid.trim()) {
      setError("Nama masjid atau surau diperlukan.");
      setStep(2);
      return false;
    }
    if (!namaAnda.trim()) {
      setError("Nama pegawai diperlukan.");
      setStep(2);
      return false;
    }
    if (!noTelAnda.trim()) {
      setError("Nombor telefon diperlukan.");
      setStep(2);
      return false;
    }
    if (!alamat.trim() || !poskod.trim() || !bandar.trim() || !negeri.trim()) {
      setError("Lengkapkan alamat penghantaran peranti ETV.");
      setStep(2);
      return false;
    }
    if (!/^\d{5}$/.test(poskod.trim())) {
      setError("Poskod mesti mengandungi 5 digit.");
      setStep(2);
      return false;
    }
    setError("");
    return true;
  };

  const validateAndRegisterUser = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");

    if (!validateStep2()) return;

    if (!agreed) {
      setError("Sila sahkan persetujuan pakej Etikaf.");
      return;
    }
    if (!recaptchaToken) {
      setError("Sila sahkan reCAPTCHA dahulu.");
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setError("Sesi pendaftaran telah tamat. Sila sahkan e-mel semula.");
      setStep(1);
      return;
    }

    setSubmitting(true);

    try {
      await reload(user);
      if (!user.emailVerified) throw new Error("E-mel belum disahkan.");
      const idToken = await user.getIdToken(true);

      const response = await fetch(REGISTRATION_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          namaMasjid: namaMasjid.trim(),
          namaAnda: namaAnda.trim(),
          noTelAnda: noTelAnda.trim(),
          alamatPenghantaran: {
            alamat: alamat.trim(),
            poskod: poskod.trim(),
            bandar: bandar.trim(),
            negeri: negeri.trim(),
          },
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.billUrl) {
        if (data?.code === "ACCOUNT_ACTIVE") {
          setError("Akaun ini sudah aktif. Sila log masuk.");
          return;
        }
        throw new Error(data?.error || "Pautan bayaran tidak dapat dijana.");
      }

      storePendingRegistration(data, user.email);
      localStorage.setItem("lastPaymentData", JSON.stringify({
        name: namaMasjid.trim(),
        email: user.email,
        amount: Number(data.totalAmount || totalAmount),
        type: "package",
      }));
      window.location.href = data.billUrl;
    } catch (err) {
      console.error("Ralat pendaftaran:", err);
      setError(err?.message || "Pendaftaran tidak berjaya. Cuba semula.");
    } finally {
      setSubmitting(false);
    }
  };

  const goNextFromDelivery = () => {
    if (validateStep2()) setStep(3);
  };

  const heading = step === 1 ? "Akaun & pengesahan e-mel" : step === 2 ? "Pegawai & penghantaran" : "Semak dan bayar";

  return (
    <div className="page-container registration-page">
      <div className="registration-shell">
        <aside className="registration-summary">
          <a className="registration-brand" href="https://etikaf.com/my">
            <img src="/images/etikaf.png" alt="Etikaf" />
          </a>
          <p className="registration-eyebrow">Pakej untuk masjid &amp; surau</p>
          <h1>Satu pendaftaran untuk seluruh ekosistem Etikaf.</h1>
          <p className="registration-lead">Sistem Admin dan peranti Etikaf TV didaftarkan bersama sebagai satu pakej lengkap.</p>

          <div className="registration-price">
            <span>Tahun pertama</span>
            <strong>RM3,600</strong>
            <small>+ penghantaran RM25 / RM50</small>
          </div>

          <ul className="registration-includes">
            <li><FaTv /><span><strong>Peranti Etikaf TV</strong>Plug and play, dihantar melalui pos</span></li>
            <li><FaCheck /><span><strong>Sistem Admin 12 bulan</strong>Urus maklumat dari telefon atau komputer</span></li>
            <li><FaMobileAlt /><span><strong>Aplikasi Etikaf untuk jemaah</strong>Percuma untuk digunakan oleh jemaah</span></li>
            <li><FaShieldAlt /><span><strong>Waranti 12 bulan</strong>Perlindungan untuk peranti ETV</span></li>
            <li><FaTruck /><span><strong>Penghantaran melalui pos</strong>RM25 Semenanjung · RM50 Sabah, Sarawak &amp; Labuan</span></li>
          </ul>

          <p className="registration-renewal">Tahun berikutnya hanya RM600 setahun untuk langganan Sistem Etikaf.</p>
        </aside>

        <main className="registration-form-card">
          <div className="registration-heading">
            <div>
              <span>Langkah {step} daripada 3</span>
              <h2>{heading}</h2>
            </div>
            <a href="/">Log masuk</a>
          </div>

          <div className="registration-progress" aria-label={`Langkah ${step} daripada 3`}>
            {[1, 2, 3].map((number) => <i key={number} className={number <= step ? "active" : ""} />)}
          </div>

          <form onSubmit={validateAndRegisterUser} className="login-form registration-form">
            {step === 1 && !verificationSent && !emailVerified && (
              <>
                <p className="registration-step-copy">Kami akan menghantar pautan untuk memastikan e-mel ini benar-benar milik anda.</p>
                <Field id="emailMasjid" label="Emel Masjid / Surau" type="email" value={emailMasjid} onChange={(e) => setEmailMasjid(e.target.value)} autoComplete="email" disabled={verificationBusy} />
                <Field id="password" label="Cipta kata laluan" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" disabled={verificationBusy} />
                <Field id="confirmPassword" label="Ulang kata laluan" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" disabled={verificationBusy} />
                <div className="registration-actions">
                  <button type="button" className="registration-back" onClick={() => { window.location.href = "https://etikaf.com/my"; }}><FaArrowLeft /> Kembali</button>
                  <button type="button" className="loginButton registration-verify-button" onClick={sendVerification} disabled={verificationBusy}>
                    {verificationBusy ? "Menghantar..." : "Sahkan e-mel"}
                  </button>
                </div>
              </>
            )}

            {step === 1 && verificationSent && (
              <div className={`email-verification-card${verificationDeliveryFailed ? " email-verification-failed" : ""}`}>
                <div className="email-verification-icon"><FaEnvelope /></div>
                <span>{verificationDeliveryFailed ? "Akaun telah dicipta" : "Pengesahan e-mel"}</span>
                <h3>{verificationDeliveryFailed ? "E-mel belum berjaya dihantar." : "Semak peti masuk anda."}</h3>
                <p>
                  {verificationDeliveryFailed
                    ? <>Akaun <strong>{emailMasjid}</strong> sudah disimpan. Cuba hantar semula pautan tanpa mendaftar sekali lagi.</>
                    : <>Kami telah menghantar pautan pengesahan ke <strong>{emailMasjid}</strong>. Klik pautan tersebut, kemudian kembali ke sini.</>}
                </p>
                {verificationDeliveryFailed ? (
                  <button type="button" className="loginButton" onClick={resendVerification} disabled={verificationBusy}>
                    <FaEnvelope /> {verificationBusy ? "Menghantar..." : "Cuba hantar semula"}
                  </button>
                ) : (
                  <>
                    <button type="button" className="loginButton" onClick={confirmVerification} disabled={verificationBusy}>
                      <FaCheckCircle /> {verificationBusy ? "Menyemak..." : "Saya sudah sahkan"}
                    </button>
                    <button type="button" className="registration-resend" onClick={resendVerification} disabled={verificationBusy}>Hantar semula pautan</button>
                  </>
                )}
                <a href="/" className="registration-existing-login">E-mel sudah mempunyai akaun? Log masuk</a>
              </div>
            )}

            {step === 1 && emailVerified && !verificationSent && (
              <div className="email-verification-card email-verification-complete">
                <div className="email-verification-icon"><FaCheckCircle /></div>
                <span>E-mel telah disahkan</span>
                <h3>{emailMasjid}</h3>
                <p>Identiti e-mel pendaftaran ini sudah disahkan dan tidak perlu diperiksa semula.</p>
                <button type="button" className="loginButton" onClick={() => setStep(2)}>Teruskan pendaftaran</button>
              </div>
            )}

            {step === 2 && (
              <>
                <div className="verified-email-row"><FaCheckCircle /><span><strong>E-mel disahkan</strong>{emailMasjid}</span></div>
                <p className="registration-step-copy">Maklumat ini digunakan untuk urusan akaun dan penghantaran peranti ETV.</p>
                <Field id="namaMasjid" label="Nama Masjid / Surau" value={namaMasjid} onChange={(e) => setNamaMasjid(e.target.value)} autoComplete="organization" />
                <div className="registration-two-columns">
                  <Field id="namaAnda" label="Nama pegawai" value={namaAnda} onChange={(e) => setNamaAnda(e.target.value)} autoComplete="name" />
                  <Field id="noTelAnda" label="Nombor telefon" type="tel" value={noTelAnda} onChange={(e) => setNoTelAnda(e.target.value)} autoComplete="tel" />
                </div>
                <Field id="alamat" label="Alamat penghantaran" value={alamat} onChange={(e) => setAlamat(e.target.value)} autoComplete="street-address" />
                <div className="registration-two-columns">
                  <Field id="poskod" label="Poskod" value={poskod} onChange={(e) => setPoskod(e.target.value)} autoComplete="postal-code" />
                  <Field id="bandar" label="Bandar" value={bandar} onChange={(e) => setBandar(e.target.value)} autoComplete="address-level2" />
                </div>
                <SelectField id="negeri" label="Pilih negeri" value={negeri} onChange={(e) => setNegeri(e.target.value)} options={MALAYSIA_STATES} />
                {negeri && <p className="registration-shipping-note"><FaTruck /> Caj penghantaran: <strong>RM{shippingFee}</strong></p>}
                <div className="registration-actions">
                  <button type="button" className="registration-back" onClick={() => setStep(1)}><FaArrowLeft /> Kembali</button>
                  <button type="button" className="loginButton" onClick={goNextFromDelivery}>Semak pesanan</button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="registration-order">
                  <div><span>Jumlah bayaran</span><strong>RM{totalAmount.toLocaleString("ms-MY")}</strong></div>
                  <ul>
                    <li>Peranti Etikaf TV — RM3,000</li>
                    <li>Langganan Sistem Etikaf 12 bulan — RM600</li>
                    <li>Aplikasi Etikaf untuk jemaah — Percuma</li>
                    <li>Penghantaran ke {negeri} — RM{shippingFee}</li>
                  </ul>
                  <div className="registration-delivery">
                    <span>Dihantar kepada</span>
                    <strong>{namaMasjid}</strong>
                    <p>{alamat}, {poskod} {bandar}, {negeri}</p>
                  </div>
                </div>

                <label className="registration-consent">
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                  <span>Saya faham Etikaf TV dan Sistem Admin ialah satu pakej yang tidak dijual berasingan.</span>
                </label>

                <div className="recaptcha-container"><ReCAPTCHA sitekey={captcha_site_key} onChange={setRecaptchaToken} /></div>

                <div className="registration-actions registration-actions-final">
                  <button type="button" className="registration-back" onClick={() => setStep(2)} disabled={submitting}><FaArrowLeft /> Kembali</button>
                  <button type="submit" className="loginButton registration-pay-button" disabled={submitting}>
                    {submitting ? "Menyediakan bayaran..." : `Bayar RM${totalAmount.toLocaleString("ms-MY")}`}
                  </button>
                </div>
                <p className="registration-secure-note">Bayaran selamat melalui Billplz. Akaun dan pesanan ETV akan direkodkan selepas bayaran berjaya.</p>
              </>
            )}

            {notice && <p className="success registration-notice">{notice}</p>}
            {error && <p className="error registration-error">{error}</p>}
          </form>
        </main>
      </div>

      <a
        className="registration-help"
        href={REGISTRATION_HELP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Dapatkan bantuan pendaftaran melalui WhatsApp"
      >
        <FaWhatsapp aria-hidden="true" />
        <span><strong>Perlukan bantuan?</strong> WhatsApp kami</span>
      </a>

      <div className="footer-text">&copy; 2017-{currentYear} Etikaf Ent.</div>
    </div>
  );
};

export default Daftar;
